import { useState, useRef, useEffect, useCallback } from 'react';
import { getId } from './helpers';

export function useTimeout (cb, delayInMs = 0) {
  const [isActive, setIsActive] = useState(false);
  const refCb = useRef(null);

  useEffect(() => {
    refCb.current = cb;
  }, [cb]);

  function start() {
    setIsActive(true);
  }

  function stop() {
    setIsActive(false);
  }

  function callback() {
    stop();
    refCb.current && refCb.current();
  }

  useEffect(() => {
    if (isActive) {
      const timeout = window.setTimeout(callback, delayInMs);
      return () => { 
        window.clearTimeout(timeout);
      }
    }
  }, [isActive]);

  return [start, stop, isActive];
}

export function useTableState (columnDefinition) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [colDef, setColDef] = useState(columnDefinition);
  const pagedData = useRef([]);
  const onRowSelect = useCallback((selected, rowData, pData) => {
    if (selected) {
      setSelectedRows([...selectedRows, rowData]);
    } else {
      setSelectedRows(selectedRows.filter(r => getId(r) !== getId(rowData)));
    }
    pagedData.current = pData;
  }, [selectedRows]);
  const onAllRowSelect = useCallback((rows, noOfNonSelectableRows, pData) => {
    if (noOfNonSelectableRows) {
      setSelectedRows(rows.filter(r => !r.default));
    } else {
      setSelectedRows(rows);
    }
    pagedData.current = pData;
  }, []);
  const onSelectedRowsReset = useCallback(() => {
    setSelectedRows([]);
  }, []);
  const onFilterChange = useCallback((filter, col) => {
    const newColDef = colDef.map(c => {
      if (c.id === col.id) {
        return { ...c, filter };
      }
      return c;
    });
    setColDef(newColDef);
    onSelectedRowsReset();
  });
  const onColumnChange = useCallback((hidden, col) => {
    const newColDef = colDef.map(c => {
      if (c.id === col.id) {
        return { ...c, hidden };
      }
      return c;
    });
    setColDef(newColDef);    
  });

  return { colDef, selectedRows, pagedData, onRowSelect, onAllRowSelect, onSelectedRowsReset, onFilterChange, onColumnChange };
}
